import { useEffect, useRef, useState } from 'react';
import { Editor } from '@tiptap/react';

import { useKeyPress } from '@/lib/hooks/use-key-press';
import { Icon } from '@/components/icon';
import { Tooltip } from '@/components/tooltip';

import styles from '../text-cell.module.scss';

interface LinkMenuProps {
  editor: Editor;
  initialHref?: string;
  hideLinkMenu: () => void;
}

export const LinkMenu = ({ editor, initialHref, hideLinkMenu }: LinkMenuProps) => {
  const [linkUrl, setLinkUrl] = useState<string>(initialHref ?? '');
  const urlInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLinkUrl(initialHref ?? '');
  }, [initialHref]);

  useEffect(() => {
    urlInputRef.current?.focus();
  }, []);

  useKeyPress(
    'escape',
    (event) => {
      event.stopPropagation();
      setLinkUrl('');
      hideLinkMenu();
    },
    { capture: true, includeInputs: true },
  );

  if (editor === null) {
    return <></>;
  }

  const handleUrlInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    saveLink();
  };

  const saveLink = () => {
    if (linkUrl === '') {
      return;
    }

    const href =
      linkUrl.startsWith('http://') || linkUrl.startsWith('https://')
        ? linkUrl
        : `https://${linkUrl}`;

    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href })
      .setTextSelection(editor.view.state.selection.to)
      .run();

    setLinkUrl('');
    hideLinkMenu();
  };

  const removeLink = () => {
    editor.chain().focus().unsetLink().run();
    hideLinkMenu();
  };

  const handleBackClick = () => {
    setLinkUrl('');
    hideLinkMenu();
  };

  return (
    <div className={styles.popupMenu}>
      <Tooltip content="Back">
        <button onMouseDown={(e) => e.preventDefault()} onClick={handleBackClick}>
          <Icon name="ArrowLeft" size={16} />
        </button>
      </Tooltip>
      <input
        type="text"
        value={linkUrl}
        onChange={(event) => setLinkUrl(event.target.value)}
        placeholder="Enter link URL"
        onKeyDown={handleUrlInputKeyDown}
        className={styles.urlInput}
        ref={urlInputRef}
      />
      <div className={styles.separator} />
      <Tooltip content="Save link">
        <button onMouseDown={(e) => e.preventDefault()} onClick={saveLink}>
          <Icon name="CornerDownLeft" size={16} />
        </button>
      </Tooltip>
      <Tooltip content="Open in new window">
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={() =>
            window.open(editor.getAttributes('link').href, '_blank', 'noopener noreferrer')
          }>
          <Icon name="ExternalLink" size={16} />
        </button>
      </Tooltip>
      <Tooltip content="Remove link">
        <button onMouseDown={(e) => e.preventDefault()} onClick={removeLink}>
          <Icon name="Trash" size={16} />
        </button>
      </Tooltip>
    </div>
  );
};
